import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { api, formatDate, formatBytes } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Folder, 
  FolderPlus,
  FolderOpen,
  PencilSimple,
  Trash,
  FileText,
  ArrowLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  CaretUpDown,
  House,
  MagnifyingGlass,
  DownloadSimple,
  ArrowsLeftRight,
  FilePdf,
  FileDoc,
  FileXls,
  FileImage,
  File as FileIcon,
  Eye,
  CheckCircle,
  DotsThreeVertical,
  Tag,
  Archive,
  TreeStructure,
  SquaresFour,
  Clock,
  XCircle,
  Receipt,
  Truck,
  CurrencyCircleDollar,
  ShoppingCart,
  ArrowCounterClockwise,
  FlowArrow,
  UploadSimple,
  X,
  Plus,
  Warning,
  ClockCounterClockwise,
  NotePencil
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const localeMap = { it: 'it-CH', de: 'de-CH', fr: 'fr-CH', en: 'en-GB' };

const getFileIcon = (contentType) => {
  if (contentType?.includes('pdf')) return FilePdf;
  if (contentType?.includes('word') || contentType?.includes('document')) return FileDoc;
  if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return FileXls;
  if (contentType?.includes('image')) return FileImage;
  return FileIcon;
};

const isPreviewable = (contentType) => {
  if (!contentType) return false;
  return contentType.includes('pdf') || 
         contentType.includes('image') || 
         contentType.includes('text');
};

// Document type styles (same as DocumentsPage)
const documentTypeStyles = {
  fattura: { icon: Receipt, bg: 'bg-blue-50', color: 'text-blue-700' },
  contratto: { icon: FileText, bg: 'bg-purple-50', color: 'text-purple-700' },
  ddt: { icon: Truck, bg: 'bg-orange-50', color: 'text-orange-700' },
  preventivo: { icon: CurrencyCircleDollar, bg: 'bg-green-50', color: 'text-green-700' },
  ordine: { icon: ShoppingCart, bg: 'bg-cyan-50', color: 'text-cyan-700' },
  nota_credito: { icon: ArrowCounterClockwise, bg: 'bg-red-50', color: 'text-red-700' },
  altro: { icon: FileText, bg: 'bg-gray-50', color: 'text-gray-600' }
};

const getDocumentTypeStyle = (type) => {
  return documentTypeStyles[type] || documentTypeStyles.altro;
};

// Single Tree Item Component
const TreeItem = ({ node, level, expandedFolders, toggleFolderExpand, onNavigate, onEdit, onDelete, t, renderChildren }) => {
  const isExpanded = expandedFolders.has(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const hasDocuments = node.document_count > 0;
  
  return (
    <div key={node.id}>
      <div 
        className="flex items-center gap-2 py-2 px-2 rounded-sm hover:bg-[#F4F4F5] transition-colors group"
        style={{ paddingLeft: `${level * 20 + 8}px` }}
      >
        {/* Expand/Collapse button */}
        <button
          onClick={() => toggleFolderExpand(node.id)}
          className={`p-0.5 rounded-sm transition-colors ${
            hasChildren ? 'hover:bg-[#E4E4E7] text-[#52525B]' : 'text-transparent'
          }`}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isExpanded ? (
              <CaretDown size={14} weight="bold" />
            ) : (
              <CaretRight size={14} weight="bold" />
            )
          ) : (
            <span className="w-[14px]" />
          )}
        </button>
        
        {/* Folder icon */}
        <div className={`w-6 h-6 flex items-center justify-center ${
          isExpanded ? 'text-[#F5B041]' : 'text-[#FFC107]'
        }`}>
          {isExpanded ? <FolderOpen size={18} weight="fill" /> : <Folder size={18} weight="fill" />}
        </div>
        
        {/* Folder name - clickable to navigate */}
        <button
          onClick={() => onNavigate(node)}
          className="flex-1 text-left text-sm font-medium text-[#09090B] hover:text-[#002FA7] transition-colors"
          data-testid={`tree-folder-${node.id}`}
        >
          {node.name}
        </button>
        
        {/* Folder stats */}
        <div className="flex items-center gap-3 text-xs text-[#A1A1AA]">
          {hasDocuments && (
            <span className="flex items-center gap-1">
              <FileText size={12} />
              {node.document_count}
            </span>
          )}
          {hasChildren && (
            <span className="flex items-center gap-1">
              <Folder size={12} />
              {node.children.length}
            </span>
          )}
        </div>
        
        {/* Actions - visible on hover */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(node);
            }}
            className="p-1 hover:bg-[#E4E4E7] rounded-sm"
            title={t('folders.edit')}
          >
            <PencilSimple size={14} className="text-[#52525B]" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(node);
            }}
            className="p-1 hover:bg-[#FFEBEE] rounded-sm"
            title={t('folders.delete')}
          >
            <Trash size={14} className="text-[#E32636]" />
          </button>
        </div>
      </div>
      
      {/* Children */}
      {isExpanded && hasChildren && renderChildren(node.children, level + 1)}
    </div>
  );
};

// Tree View Component
const FolderTree = ({ nodes, level, expandedFolders, toggleFolderExpand, onNavigate, onEdit, onDelete, t }) => {
  const renderNodes = (nodeList, currentLevel) => {
    // Sort nodes alphabetically by name
    const sortedNodes = [...nodeList].sort((a, b) => a.name.localeCompare(b.name));
    return (
      <div className="space-y-1">
        {sortedNodes.map(node => (
          <TreeItem
            key={node.id}
            node={node}
            level={currentLevel}
            expandedFolders={expandedFolders}
            toggleFolderExpand={toggleFolderExpand}
            onNavigate={onNavigate}
            onEdit={onEdit}
            onDelete={onDelete}
            t={t}
            renderChildren={renderNodes}
          />
        ))}
      </div>
    );
  };
  
  return renderNodes(nodes, level);
};

export const FoldersPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', parent_id: null });
  
  // Document view/preview state
  const [viewDoc, setViewDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPdfaDoc, setPreviewPdfaDoc] = useState(null);
  const [previewPdfaUrl, setPreviewPdfaUrl] = useState(null);
  
  // Edit tags state
  const [editTagsDoc, setEditTagsDoc] = useState(null);
  const [editedTags, setEditedTags] = useState('');
  
  // Document search
  const [docSearch, setDocSearch] = useState('');
  
  // Move document state
  const [moveDoc, setMoveDoc] = useState(null);
  const [targetFolderId, setTargetFolderId] = useState('root');
  const [moving, setMoving] = useState(false);
  
  // Drag and drop state
  const [draggedDoc, setDraggedDoc] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  
  // Tree view state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'tree'
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [treeData, setTreeData] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);

  // Workflow history state
  const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
  const [workflowHistory, setWorkflowHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Sorting state for documents
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTags, setUploadTags] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Version/Duplicate detection state
  const [duplicateFile, setDuplicateFile] = useState(null);
  const [duplicateExisting, setDuplicateExisting] = useState(null);
  const [pendingUploads, setPendingUploads] = useState([]);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Sort documents
  const sortedDocuments = [...documents].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'original_filename') {
      aVal = aVal?.toLowerCase() || '';
      bVal = bVal?.toLowerCase() || '';
    } else if (sortField === 'size' || sortField === 'classification_confidence') {
      aVal = aVal || 0;
      bVal = bVal || 0;
    } else if (sortField === 'document_type_label') {
      aVal = aVal || 'altro';
      bVal = bVal || 'altro';
    } else if (sortField === 'workflow_status') {
      aVal = aVal || '';
      bVal = bVal || '';
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Fetch folders at current level
  const fetchFolders = useCallback(async (parentId = null) => {
    try {
      const res = await api.getFolders(parentId);
      setFolders(res.data);
    } catch (error) {
      toast.error(t('common.error'));
    }
  }, [t]);

  // Fetch documents in current folder
  const fetchDocuments = useCallback(async (folderId = null, search = '') => {
    try {
      const res = await api.getDocuments(folderId, search || null);
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }, []);

  // Fetch all folders for the select dropdown
  const fetchAllFolders = async () => {
    try {
      const res = await api.getAllFolders();
      setAllFolders(res.data);
    } catch (error) {
      console.error('Failed to fetch all folders:', error);
    }
  };

  // Fetch folder details for breadcrumb
  const fetchFolderDetails = useCallback(async (folderId) => {
    try {
      const res = await api.getFolder(folderId);
      setCurrentFolder(res.data);
      setBreadcrumb([...res.data.path, { id: res.data.id, name: res.data.name }]);
    } catch (error) {
      console.error('Failed to fetch folder details:', error);
    }
  }, []);

  // Build tree structure from flat folder list
  const buildTreeStructure = useCallback((folders) => {
    const folderMap = new Map();
    const rootFolders = [];
    
    // First pass: create map of all folders
    folders.forEach(folder => {
      folderMap.set(folder.id, { ...folder, children: [] });
    });
    
    // Second pass: build hierarchy
    folders.forEach(folder => {
      const node = folderMap.get(folder.id);
      if (folder.parent_id && folderMap.has(folder.parent_id)) {
        folderMap.get(folder.parent_id).children.push(node);
      } else {
        rootFolders.push(node);
      }
    });
    
    return rootFolders;
  }, []);

  // Fetch tree data when switching to tree view
  const fetchTreeData = useCallback(async () => {
    setLoadingTree(true);
    try {
      const res = await api.getAllFolders();
      const tree = buildTreeStructure(res.data);
      setTreeData(tree);
    } catch (error) {
      console.error('Failed to fetch tree data:', error);
    } finally {
      setLoadingTree(false);
    }
  }, [buildTreeStructure]);

  // Toggle folder expansion in tree view
  const toggleFolderExpand = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Expand all folders in tree
  const expandAllFolders = () => {
    const allIds = new Set(allFolders.map(f => f.id));
    setExpandedFolders(allIds);
  };

  // Collapse all folders in tree
  const collapseAllFolders = () => {
    setExpandedFolders(new Set());
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === 'tree') {
      fetchTreeData();
    }
  };

  // Navigate to folder from tree
  const navigateToFolderFromTree = async (folder) => {
    setViewMode('grid');
    if (folder.parent_id) {
      await fetchFolderDetails(folder.id);
    }
    setCurrentFolder(folder);
  };

  useEffect(() => {
    fetchAllFolders();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (currentFolder) {
        await Promise.all([
          fetchFolders(currentFolder.id),
          fetchFolderDetails(currentFolder.id),
          fetchDocuments(currentFolder.id, docSearch)
        ]);
      } else {
        await Promise.all([
          fetchFolders(null),
          fetchDocuments(null, docSearch)
        ]);
        setBreadcrumb([]);
      }
      setLoading(false);
    };
    loadData();
  }, [currentFolder?.id, fetchFolders, fetchFolderDetails, fetchDocuments]);

  // Search documents with debounce
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchDocuments(currentFolder?.id || null, docSearch);
    }, 300);
    return () => clearTimeout(debounce);
  }, [docSearch, currentFolder?.id, fetchDocuments]);

  // Navigate to folder
  const openFolder = (folder) => {
    setCurrentFolder(folder);
    setDocSearch('');
  };

  // Navigate back
  const goBack = () => {
    if (breadcrumb.length > 1) {
      const parentId = breadcrumb[breadcrumb.length - 2].id;
      const parentFolder = allFolders.find(f => f.id === parentId);
      setCurrentFolder(parentFolder || null);
    } else {
      setCurrentFolder(null);
    }
    setDocSearch('');
  };

  // Navigate to specific breadcrumb
  const goToBreadcrumb = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
    } else {
      const folder = allFolders.find(f => f.id === breadcrumb[index].id);
      setCurrentFolder(folder || null);
    }
    setDocSearch('');
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    try {
      await api.createFolder({
        name: formData.name,
        description: formData.description || null,
        parent_id: formData.parent_id || (currentFolder?.id || null)
      });
      toast.success(t('common.success'));
      setShowCreateDialog(false);
      setFormData({ name: '', description: '', parent_id: null });
      fetchFolders(currentFolder?.id || null);
      fetchAllFolders();
      if (currentFolder) fetchFolderDetails(currentFolder.id);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleUpdate = async () => {
    if (!editFolder || !formData.name.trim()) return;
    try {
      await api.updateFolder(editFolder.id, {
        name: formData.name,
        description: formData.description || null,
        parent_id: formData.parent_id || null
      });
      toast.success(t('common.success'));
      setEditFolder(null);
      setFormData({ name: '', description: '', parent_id: null });
      fetchFolders(currentFolder?.id || null);
      fetchAllFolders();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteFolder) return;
    try {
      await api.deleteFolder(deleteFolder.id);
      toast.success(t('common.success'));
      setDeleteFolder(null);
      fetchFolders(currentFolder?.id || null);
      fetchAllFolders();
      if (currentFolder) fetchFolderDetails(currentFolder.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  // Load workflow history for a document
  const loadWorkflowHistory = async (doc) => {
    if (!doc.workflow_id) {
      toast.error('Nessun workflow associato a questo documento');
      return;
    }
    
    setLoadingHistory(true);
    try {
      const res = await api.getWorkflow(doc.workflow_id);
      setWorkflowHistory(res.data);
      setShowWorkflowHistory(true);
    } catch (error) {
      toast.error('Errore nel caricamento dello storico');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Move document handlers
  const handleMoveDocument = async () => {
    if (!moveDoc) return;
    setMoving(true);
    try {
      const folderId = targetFolderId === 'root' ? null : targetFolderId;
      await api.moveDocument(moveDoc.id, folderId);
      toast.success(t('folders.documentMoved'));
      setMoveDoc(null);
      setTargetFolderId('root');
      fetchDocuments(currentFolder?.id || null, docSearch);
      fetchFolders(currentFolder?.id || null);
      if (currentFolder) fetchFolderDetails(currentFolder.id);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setMoving(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, doc) => {
    setDraggedDoc(doc);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', doc.id);
  };

  const handleDragEnd = () => {
    setDraggedDoc(null);
    setDragOverFolder(null);
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderId);
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolder(null);
    
    if (!draggedDoc) return;
    
    try {
      await api.moveDocument(draggedDoc.id, folderId);
      toast.success(t('folders.documentMoved'));
      fetchDocuments(currentFolder?.id || null, docSearch);
      fetchFolders(currentFolder?.id || null);
      if (currentFolder) fetchFolderDetails(currentFolder.id);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setDraggedDoc(null);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await api.downloadDocument(doc.id, doc.original_filename);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDownloadPdfa = async (doc) => {
    try {
      await api.downloadPdfA(doc.id, doc.original_filename);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handlePreview = async (doc) => {
    try {
      const blob = await api.getPreviewBlob(doc.id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewDoc(doc);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewDoc(null);
  };

  const handlePreviewPdfa = (doc) => {
    if (!doc.has_pdfa) {
      toast.error(t('documents.pdfaNotAvailable'));
      return;
    }
    const pdfaUrl = api.getDocumentPreviewPdfaUrl(doc.id);
    setPreviewPdfaDoc(doc);
    setPreviewPdfaUrl(pdfaUrl);
  };

  const closePreviewPdfa = () => {
    setPreviewPdfaDoc(null);
    setPreviewPdfaUrl(null);
  };

  const openEditTags = (doc) => {
    setEditedTags(doc.tags ? doc.tags.join(', ') : '');
    setEditTagsDoc(doc);
  };

  const handleSaveTags = async () => {
    if (!editTagsDoc) return;
    try {
      const tagsArray = editedTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
      await api.updateDocumentTags(editTagsDoc.id, tagsArray);
      toast.success(t('documents.tagsUpdated'));
      setEditTagsDoc(null);
      fetchDocuments(currentFolder?.id || null, docSearch);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteDoc = async (doc) => {
    try {
      await api.deleteDocument(doc.id);
      toast.success(t('common.success'));
      fetchDocuments(currentFolder?.id || null, docSearch);
      if (currentFolder) fetchFolderDetails(currentFolder.id);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Helper function to calculate file hash
  const calculateFileHash = async (file) => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Upload handlers
  const handleUpload = async () => {
    if (uploadFiles.length === 0 || !currentFolder) return;
    
    setUploading(true);
    const tagsArray = uploadTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
    const tagsString = tagsArray.join(',');
    
    try {
      for (const file of uploadFiles) {
        // Calculate hash for duplicate detection
        const fileHash = await calculateFileHash(file);
        
        // Check for duplicates
        try {
          const checkRes = await api.checkDocumentDuplicate(file.name, currentFolder.id, fileHash);
          const { exists, existing_document, is_same_content } = checkRes.data;
          
          if (exists && existing_document) {
            if (is_same_content) {
              // Same content - just skip with notification
              toast.info(`${file.name}: ${t('documents.duplicateSameContent')}`);
              continue;
            }
            
            // Different content, same name - ask for confirmation
            setDuplicateFile(file);
            setDuplicateExisting(existing_document);
            setPendingUploads(uploadFiles.filter(f => f !== file));
            setUploading(false);
            return; // Wait for user decision
          }
        } catch (checkErr) {
          console.error('Check duplicate error:', checkErr);
          // Continue with upload if check fails
        }
        
        // No duplicate - upload normally
        await api.uploadDocument(file, currentFolder.id, tagsString);
      }
      
      toast.success(t('documents.uploadSuccess'));
      fetchDocuments(currentFolder.id, docSearch);
      fetchFolderDetails(currentFolder.id);
      setShowUploadDialog(false);
      setUploadFiles([]);
      setUploadTags('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Handle duplicate resolution
  const handleDuplicateDecision = async (decision) => {
    const tagsArray = uploadTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
    const tagsString = tagsArray.join(',');
    
    setUploading(true);
    
    try {
      if (decision === 'update') {
        // Update existing with new version
        await api.uploadDocument(duplicateFile, currentFolder.id, tagsString, duplicateExisting.id);
        toast.success(`${t('documents.versionUpdated')} ${duplicateExisting.version + 1}`);
      } else if (decision === 'skip') {
        // Skip this file
        toast.info(`${duplicateFile.name}: Saltato`);
      }
      
      // Reset duplicate state
      setDuplicateFile(null);
      setDuplicateExisting(null);
      
      // Continue with remaining files
      if (pendingUploads.length > 0) {
        for (const file of pendingUploads) {
          const fileHash = await calculateFileHash(file);
          
          try {
            const checkRes = await api.checkDocumentDuplicate(file.name, currentFolder.id, fileHash);
            const { exists, existing_document, is_same_content } = checkRes.data;
            
            if (exists && existing_document) {
              if (is_same_content) {
                toast.info(`${file.name}: ${t('documents.duplicateSameContent')}`);
                continue;
              }
              setDuplicateFile(file);
              setDuplicateExisting(existing_document);
              setPendingUploads(pendingUploads.filter(f => f !== file));
              setUploading(false);
              return;
            }
          } catch (checkErr) {
            console.error('Check duplicate error:', checkErr);
          }
          
          await api.uploadDocument(file, currentFolder.id, tagsString);
        }
      }
      
      setPendingUploads([]);
      fetchDocuments(currentFolder.id, docSearch);
      fetchFolderDetails(currentFolder.id);
      setShowUploadDialog(false);
      setUploadFiles([]);
      setUploadTags('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Fetch document versions
  const handleViewVersions = async (doc) => {
    setVersionHistoryDoc(doc);
    setShowVersionHistory(true);
    setLoadingVersions(true);
    
    try {
      const res = await api.getDocumentVersions(doc.id);
      setDocumentVersions(res.data);
    } catch (error) {
      toast.error(t('common.error'));
      setDocumentVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Download specific version
  const handleDownloadVersion = async (versionId) => {
    try {
      const doc = documentVersions.find(v => v.id === versionId);
      if (doc) {
        await api.downloadDocument(versionId, `v${doc.version}_${versionHistoryDoc.original_filename}`);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openEditDialog = useCallback((folder) => {
    setFormData({
      name: folder.name,
      description: folder.description || '',
      parent_id: folder.parent_id || null
    });
    setEditFolder(folder);
  }, []);

  const openCreateDialog = () => {
    setFormData({ 
      name: '', 
      description: '', 
      parent_id: currentFolder?.id || null 
    });
    setShowCreateDialog(true);
  };

  const handleSetDeleteFolder = useCallback((folder) => {
    setDeleteFolder(folder);
  }, []);

  // Filter documents in current folder only
  const filteredDocuments = documents.filter(doc => {
    const docFolderId = doc.folder_id || null;
    const currentFolderId = currentFolder?.id || null;
    return docFolderId === currentFolderId;
  });

  return (
    <Layout>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {currentFolder && viewMode === 'grid' && (
              <Button
                variant="outline"
                size="sm"
                onClick={goBack}
                className="rounded-sm"
                data-testid="back-btn"
              >
                <ArrowLeft size={18} className="mr-1" />
                {t('folders.back')}
              </Button>
            )}
            <h1 className="page-title" data-testid="folders-title">
              {currentFolder ? currentFolder.name : t('folders.title')}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center bg-[#F4F4F5] rounded-sm p-0.5">
              <button
                onClick={() => handleViewModeChange('grid')}
                className={`p-2 rounded-sm transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white text-[#002FA7] shadow-sm' 
                    : 'text-[#52525B] hover:text-[#09090B]'
                }`}
                title={t('folders.gridView')}
                data-testid="grid-view-btn"
              >
                <SquaresFour size={18} weight={viewMode === 'grid' ? 'fill' : 'regular'} />
              </button>
              <button
                onClick={() => handleViewModeChange('tree')}
                className={`p-2 rounded-sm transition-colors ${
                  viewMode === 'tree' 
                    ? 'bg-white text-[#002FA7] shadow-sm' 
                    : 'text-[#52525B] hover:text-[#09090B]'
                }`}
                title={t('folders.treeView')}
                data-testid="tree-view-btn"
              >
                <TreeStructure size={18} weight={viewMode === 'tree' ? 'fill' : 'regular'} />
              </button>
            </div>
            
            <Button
              onClick={openCreateDialog}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="create-folder-btn"
            >
              <FolderPlus size={20} className="mr-2" />
              {t('folders.create')}
            </Button>
          </div>
        </div>

        {/* Breadcrumb - only in grid view */}
        {viewMode === 'grid' && breadcrumb.length > 0 && (
          <div className="flex items-center gap-1 mt-4 text-sm">
            <button
              onClick={() => goToBreadcrumb(-1)}
              className="flex items-center gap-1 text-[#002FA7] hover:underline"
              data-testid="breadcrumb-root"
            >
              <House size={16} />
              {t('folders.root')}
            </button>
            {breadcrumb.map((item, index) => (
              <div key={item.id} className="flex items-center gap-1">
                <CaretRight size={14} className="text-[#A1A1AA]" />
                <button
                  onClick={() => goToBreadcrumb(index)}
                  className={`hover:underline ${
                    index === breadcrumb.length - 1 
                      ? 'text-[#09090B] font-medium' 
                      : 'text-[#002FA7]'
                  }`}
                  data-testid={`breadcrumb-${item.id}`}
                >
                  {item.name}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="content-area">
        {/* Tree View */}
        {viewMode === 'tree' ? (
          <div className="bg-white border border-[#E4E4E7]">
            {/* Tree header */}
            <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
              <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('folders.treeStructure')}
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={expandAllFolders}
                  className="text-xs text-[#52525B]"
                  data-testid="expand-all-btn"
                >
                  {t('folders.expandAll')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={collapseAllFolders}
                  className="text-xs text-[#52525B]"
                  data-testid="collapse-all-btn"
                >
                  {t('folders.collapseAll')}
                </Button>
              </div>
            </div>
            
            {/* Tree content */}
            <div className="p-4">
              {loadingTree ? (
                <div className="text-center py-8 text-[#52525B]">
                  {t('common.loading')}
                </div>
              ) : treeData.length === 0 ? (
                <div className="text-center py-8">
                  <Folder size={40} className="mx-auto text-[#A1A1AA] mb-2" />
                  <p className="text-sm text-[#52525B]">{t('folders.noFolders')}</p>
                </div>
              ) : (
                <FolderTree 
                  nodes={treeData} 
                  level={0}
                  expandedFolders={expandedFolders}
                  toggleFolderExpand={toggleFolderExpand}
                  onNavigate={navigateToFolderFromTree}
                  onEdit={openEditDialog}
                  onDelete={handleSetDeleteFolder}
                  t={t}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Current folder info - Grid view */}
            {currentFolder && (
              <div className="bg-white border border-[#E4E4E7] p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFF3E0] flex items-center justify-center">
                    <FolderOpen size={24} className="text-[#F5B041]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#09090B]">{currentFolder.name}</p>
                    {currentFolder.description && (
                      <p className="text-sm text-[#52525B]">{currentFolder.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-[#52525B]" />
                    <span className="text-[#52525B]">{currentFolder.document_count} {t('folders.documents')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Folder size={16} className="text-[#52525B]" />
                    <span className="text-[#52525B]">{currentFolder.subfolder_count} {t('folders.subfolders')}</span>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="p-8 text-center text-[#52525B]">
                {t('common.loading')}
              </div>
            ) : (
              <>
                {/* Subfolders Grid */}
                {folders.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-[#52525B] mb-4">
                      {t('folders.subfolders')}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[...folders].sort((a, b) => a.name.localeCompare(b.name)).map(folder => (
                        <div 
                          key={folder.id}
                          className={`bg-white border transition-all duration-200 ${
                            dragOverFolder === folder.id 
                              ? 'border-[#002FA7] border-2 bg-[#E3F2FD]' 
                              : 'border-[#E4E4E7] hover:border-[#09090B]'
                          }`}
                          data-testid={`folder-${folder.id}`}
                          onDragOver={(e) => handleDragOver(e, folder.id)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, folder.id)}
                        >
                          {/* Folder Header - Clickable to open */}
                          <div 
                        className="p-4 cursor-pointer hover:bg-[#F4F4F5] transition-colors"
                        onClick={() => openFolder(folder)}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-[#FFF3E0] flex items-center justify-center">
                            <Folder size={20} className="text-[#F5B041]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#09090B] truncate">{folder.name}</p>
                            {folder.description && (
                              <p className="text-xs text-[#52525B] truncate">{folder.description}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Stats */}
                        <div className="flex items-center gap-4 text-xs text-[#52525B]">
                          <div className="flex items-center gap-1">
                            <FileText size={14} />
                            <span>{folder.document_count} {t('folders.documents')}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Folder size={14} />
                            <span>{folder.subfolder_count} {t('folders.subfolders')}</span>
                          </div>
                        </div>
                        
                        {/* Drop indicator */}
                        {dragOverFolder === folder.id && (
                          <div className="mt-2 text-xs text-[#002FA7] font-medium text-center">
                            {t('folders.dropHere')}
                          </div>
                        )}
                      </div>
                      
                      {/* Actions Footer */}
                      <div className="flex items-center justify-between px-4 py-2 border-t border-[#E4E4E7] bg-[#F4F4F5]">
                        <span className="text-xs text-[#A1A1AA]">
                          {formatDate(folder.created_at, localeMap[language])}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openFolder(folder);
                            }}
                            className="p-1.5 hover:bg-white transition-colors rounded-sm"
                            title={t('folders.open')}
                            data-testid={`open-folder-${folder.id}`}
                          >
                            <FolderOpen size={16} className="text-[#002FA7]" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(folder);
                            }}
                            className="p-1.5 hover:bg-white transition-colors rounded-sm"
                            title={t('folders.edit')}
                            data-testid={`edit-folder-${folder.id}`}
                          >
                            <PencilSimple size={16} className="text-[#52525B]" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDeleteFolder(folder);
                            }}
                            className="p-1.5 hover:bg-[#FFEBEE] transition-colors rounded-sm"
                            title={t('folders.delete')}
                            data-testid={`delete-folder-${folder.id}`}
                          >
                            <Trash size={16} className="text-[#E32636]" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('folders.documentsInFolder') || t('folders.documents')}
                </h2>
                <div className="flex items-center gap-3">
                  <div className="relative w-64">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                    <Input
                      placeholder={t('folders.searchInFolder')}
                      value={docSearch}
                      onChange={(e) => setDocSearch(e.target.value)}
                      className="pl-9 h-9 text-sm input-swiss"
                      data-testid="folder-doc-search"
                    />
                  </div>
                  <Button
                    onClick={() => setShowUploadDialog(true)}
                    className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm h-9"
                    data-testid="upload-document-folder-btn"
                  >
                    <UploadSimple size={18} className="mr-2" />
                    {t('documents.upload')}
                  </Button>
                </div>
              </div>

              {filteredDocuments.length === 0 ? (
                <div className="bg-white border border-[#E4E4E7] p-8 text-center">
                  <FileText size={32} className="mx-auto text-[#A1A1AA] mb-2" />
                  <p className="text-sm text-[#52525B]">{t('documents.noDocuments')}</p>
                </div>
              ) : (
                <div className="bg-white border border-[#E4E4E7]">
                  <table className="w-full table-swiss">
                    <thead>
                      <tr>
                        <th className="text-left px-4 py-3 cursor-pointer hover:bg-[#F4F4F5] select-none" onClick={() => handleSort('original_filename')}>
                          <div className="flex items-center gap-1">
                            {t('documents.name')}
                            {sortField === 'original_filename' ? (sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                          </div>
                        </th>
                        <th className="text-left px-4 py-3 cursor-pointer hover:bg-[#F4F4F5] select-none" onClick={() => handleSort('size')}>
                          <div className="flex items-center gap-1">
                            {t('documents.size')}
                            {sortField === 'size' ? (sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                          </div>
                        </th>
                        <th className="text-left px-4 py-3 cursor-pointer hover:bg-[#F4F4F5] select-none" onClick={() => handleSort('document_type_label')}>
                          <div className="flex items-center gap-1">
                            {t('documents.documentType')}
                            {sortField === 'document_type_label' ? (sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                          </div>
                        </th>
                        <th className="text-left px-4 py-3 cursor-pointer hover:bg-[#F4F4F5] select-none" onClick={() => handleSort('has_pdfa')}>
                          <div className="flex items-center gap-1">
                            {t('documents.pdfa')}
                            {sortField === 'has_pdfa' ? (sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                          </div>
                        </th>
                        <th className="text-left px-4 py-3 cursor-pointer hover:bg-[#F4F4F5] select-none" onClick={() => handleSort('created_at')}>
                          <div className="flex items-center gap-1">
                            {t('documents.date')}
                            {sortField === 'created_at' ? (sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                          </div>
                        </th>
                        <th className="text-left px-4 py-3 cursor-pointer hover:bg-[#F4F4F5] select-none" onClick={() => handleSort('workflow_status')}>
                          <div className="flex items-center gap-1">
                            {t('documents.status')}
                            {sortField === 'workflow_status' ? (sortDirection === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />) : <CaretUpDown size={14} className="text-[#A1A1AA]" />}
                          </div>
                        </th>
                        <th className="text-right px-4 py-3">{t('documents.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDocuments.filter(d => !docSearch || d.original_filename.toLowerCase().includes(docSearch.toLowerCase())).map((doc) => {
                        const Icon = getFileIcon(doc.content_type);
                        const canPreview = isPreviewable(doc.content_type);
                        const typeStyle = getDocumentTypeStyle(doc.document_type_label || 'altro');
                        const TypeIcon = typeStyle.icon;
                        return (
                          <tr key={doc.id} data-testid={`doc-row-${doc.id}`} draggable onDragStart={(e) => handleDragStart(e, doc)} onDragEnd={handleDragEnd} className={`cursor-grab ${draggedDoc?.id === doc.id ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-[#F4F4F5] flex items-center justify-center rounded-sm"><Icon size={16} /></div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#09090B]">{doc.original_filename}</span>
                                    {doc.version > 1 && (
                                      <Badge 
                                        variant="outline" 
                                        className="text-[10px] px-1.5 py-0 text-[#002FA7] border-[#002FA7] cursor-pointer hover:bg-blue-50"
                                        onClick={() => handleViewVersions(doc)}
                                        title={t('documents.viewVersions')}
                                      >
                                        v{doc.version}
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="hash-display text-[10px] mt-0.5 block" title={doc.hash_sha256}>{doc.hash_sha256?.substring(0, 16)}...</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#52525B]">{formatBytes(doc.size)}</td>
                            <td className="px-4 py-3">
                              <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm ${typeStyle.bg}`}>
                                <TypeIcon size={14} className={typeStyle.color} weight="fill" />
                                <span className={`text-xs font-medium ${typeStyle.color}`}>{t(`documents.types.${doc.document_type_label || 'altro'}`)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {doc.has_pdfa ? (
                                <div className="flex items-center gap-1">
                                  <CheckCircle size={16} className="text-green-600" weight="fill" />
                                  <span className="text-xs text-green-700 font-medium">PDF/A-1b</span>
                                  {doc.pdfa_size && <span className="text-[10px] text-[#A1A1AA] ml-1">({formatBytes(doc.pdfa_size)})</span>}
                                </div>
                              ) : <span className="text-xs text-[#A1A1AA]">—</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-[#52525B]">{formatDate(doc.created_at, localeMap[language])}</td>
                            <td className="px-4 py-3">
                              {doc.workflow_status === 'pending' && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded"><Clock size={12} />{t('documents.workflowStatus.pending')}</span>}
                              {doc.workflow_status === 'changes_requested' && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded"><PencilSimple size={12} />{t('documents.workflowStatus.changes_requested')}</span>}
                              {(doc.workflow_status === 'approved' || doc.workflow_status === 'archived') && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded"><CheckCircle size={12} weight="fill" />{t('documents.workflowStatus.approved')}</span>}
                              {doc.workflow_status === 'rejected' && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded"><XCircle size={12} />{t('documents.workflowStatus.rejected')}</span>}
                              {!doc.workflow_status && <span className="text-xs text-[#A1A1AA]">{t('documents.workflowStatus.none')}</span>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                {/* View Details - Always visible */}
                                <button
                                  onClick={() => setViewDoc(doc)}
                                  className="p-1.5 hover:bg-[#F4F4F5] transition-colors rounded-sm"
                                  title={t('documents.view')}
                                  data-testid={`view-doc-${doc.id}`}
                                >
                                  <FileText size={16} className="text-[#52525B]" />
                                </button>
                                
                                {/* Preview - Always visible if previewable */}
                                {canPreview && (
                                  <button
                                    onClick={() => handlePreview(doc)}
                                    className="p-1.5 hover:bg-[#F4F4F5] transition-colors rounded-sm"
                                    title={t('documents.preview')}
                                    data-testid={`preview-doc-${doc.id}`}
                                  >
                                    <Eye size={16} className="text-[#52525B]" />
                                  </button>
                                )}
                                
                                {/* Three dots menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      className="p-1.5 hover:bg-[#F4F4F5] transition-colors rounded-sm"
                                      data-testid={`doc-menu-${doc.id}`}
                                    >
                                      <DotsThreeVertical size={16} className="text-[#52525B]" weight="bold" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem 
                                      onClick={() => {
                                        setMoveDoc(doc);
                                        setTargetFolderId(currentFolder?.id || 'root');
                                      }}
                                      className="cursor-pointer"
                                    >
                                      <ArrowsLeftRight size={16} className="mr-2 text-[#52525B]" />
                                      {t('folders.moveDocument')}
                                    </DropdownMenuItem>
                                    
                                    <DropdownMenuSeparator />
                                    
                                    <DropdownMenuItem 
                                      onClick={() => handleDownload(doc)}
                                      className="cursor-pointer"
                                    >
                                      <DownloadSimple size={16} className="mr-2 text-[#002FA7]" />
                                      {t('documents.download')}
                                    </DropdownMenuItem>
                                    
                                    {doc.has_pdfa && (
                                      <DropdownMenuItem 
                                        onClick={() => handleDownloadPdfa(doc)}
                                        className="cursor-pointer"
                                      >
                                        <Archive size={16} className="mr-2 text-green-600" />
                                        {t('documents.downloadPdfa')}
                                      </DropdownMenuItem>
                                    )}
                                    
                                    <DropdownMenuSeparator />
                                    
                                    <DropdownMenuItem 
                                      onClick={() => openEditTags(doc)}
                                      className="cursor-pointer"
                                    >
                                      <Tag size={16} className="mr-2 text-[#52525B]" />
                                      {t('documents.editTags')}
                                    </DropdownMenuItem>
                                    
                                    {/* Version History */}
                                    <DropdownMenuItem 
                                      onClick={() => handleViewVersions(doc)}
                                      className="cursor-pointer"
                                    >
                                      <ClockCounterClockwise size={16} className="mr-2 text-[#52525B]" />
                                      {t('documents.viewVersions')}
                                      {doc.version > 1 && (
                                        <Badge variant="outline" className="ml-auto text-xs">
                                          v{doc.version}
                                        </Badge>
                                      )}
                                    </DropdownMenuItem>
                                    
                                    {/* Meta Note - only for PDF or documents with PDF/A */}
                                    {(doc.has_pdfa || doc.content_type === 'application/pdf' || 
                                      doc.original_filename?.toLowerCase().endsWith('.pdf')) && (
                                      <DropdownMenuItem 
                                        onClick={() => navigate(`/metanote/${doc.id}`)}
                                        className="cursor-pointer"
                                      >
                                        <NotePencil size={16} className="mr-2 text-[#002FA7]" />
                                        {t('metanote.openMetaNote') || 'Meta Note'}
                                      </DropdownMenuItem>
                                    )}
                                    
                                    <DropdownMenuSeparator />
                                    
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteDoc(doc)}
                                      className="cursor-pointer text-[#E32636] focus:text-[#E32636]"
                                    >
                                      <Trash size={16} className="mr-2" />
                                      {t('documents.delete')}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Empty state for root with no folders */}
            {folders.length === 0 && filteredDocuments.length === 0 && !currentFolder && (
              <div className="bg-white border border-[#E4E4E7]">
                <div className="empty-state py-16">
                  <Folder className="empty-state-icon" size={48} />
                  <p className="empty-state-title">{t('folders.noFolders')}</p>
                  <p className="empty-state-text">{t('folders.createFirst')}</p>
                  <Button
                    onClick={openCreateDialog}
                    className="mt-4 bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                    data-testid="create-first-folder"
                  >
                    {t('folders.create')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>

      {/* Create/Edit Folder Dialog */}
      <Dialog 
        open={showCreateDialog || !!editFolder} 
        onOpenChange={() => {
          setShowCreateDialog(false);
          setEditFolder(null);
          setFormData({ name: '', description: '', parent_id: null });
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editFolder ? t('folders.edit') : t('folders.create')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('folders.name')}
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-swiss mt-1"
                data-testid="folder-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('folders.description')}
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-swiss mt-1"
                rows={3}
                data-testid="folder-description-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('folders.parentFolder')}
              </Label>
              <Select 
                value={formData.parent_id || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, parent_id: v === 'none' ? null : v })}
              >
                <SelectTrigger className="mt-1 rounded-sm" data-testid="parent-folder-select">
                  <SelectValue placeholder={t('folders.root')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('folders.root')}</SelectItem>
                  {allFolders
                    .filter(f => f.id !== editFolder?.id)
                    .map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setEditFolder(null);
              }}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={editFolder ? handleUpdate : handleCreate}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="save-folder-btn"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Document Dialog */}
      <Dialog open={!!moveDoc} onOpenChange={() => setMoveDoc(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsLeftRight size={20} />
              {t('folders.moveDocument')}
            </DialogTitle>
          </DialogHeader>
          {moveDoc && (
            <div className="space-y-4">
              <p className="text-sm text-[#52525B]">
                {moveDoc.original_filename}
              </p>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('folders.moveToFolder')}
                </Label>
                <Select value={targetFolderId} onValueChange={setTargetFolderId}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="move-target-folder">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">{t('folders.root')}</SelectItem>
                    {[...allFolders].sort((a, b) => a.name.localeCompare(b.name)).map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMoveDoc(null)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleMoveDocument}
              disabled={moving}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
              data-testid="confirm-move-btn"
            >
              {moving ? t('common.loading') : t('folders.moveDocument')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation */}
      <AlertDialog open={!!deleteFolder} onOpenChange={() => setDeleteFolder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('folders.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#E32636] hover:bg-[#C62828] text-white"
              data-testid="confirm-delete-folder"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Document Details Dialog */}
      <Dialog open={!!viewDoc} onOpenChange={() => setViewDoc(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">
              {viewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          {viewDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.size')}
                  </p>
                  <p className="text-sm text-[#09090B]">{formatBytes(viewDoc.size)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.version')}
                  </p>
                  <p className="text-sm text-[#09090B]">v{viewDoc.version}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    {t('documents.date')}
                  </p>
                  <p className="text-sm text-[#09090B]">
                    {formatDate(viewDoc.created_at, localeMap[language])}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                    Content Type
                  </p>
                  <p className="text-sm text-[#09090B]">{viewDoc.content_type}</p>
                </div>
              </div>
              
              {/* PDF/A Status */}
              <div className="p-3 bg-[#F4F4F5] rounded-sm">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2">
                  {t('documents.archiveVersion')}
                </p>
                {viewDoc.has_pdfa ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} className="text-green-600" weight="fill" />
                      <span className="text-sm font-medium text-green-700">
                        {t('documents.pdfaAvailable')}
                      </span>
                      {viewDoc.pdfa_size && (
                        <span className="text-xs text-[#52525B]">
                          ({formatBytes(viewDoc.pdfa_size)})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handlePreviewPdfa(viewDoc)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-green-700 border-green-600 hover:bg-green-50"
                      >
                        <Eye size={16} className="mr-1" />
                        {t('documents.preview')}
                      </Button>
                      <Button
                        onClick={() => handleDownloadPdfa(viewDoc)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-green-700 border-green-600 hover:bg-green-50"
                      >
                        <Archive size={16} className="mr-1" />
                        {t('documents.downloadPdfa')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-[#A1A1AA]">{t('documents.pdfaNotAvailable')}</span>
                )}
              </div>
              
              {/* Tags */}
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2">
                  {t('documents.tags')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {viewDoc.tags && viewDoc.tags.length > 0 ? (
                    viewDoc.tags.map((tag, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary"
                        className="bg-[#E3F2FD] text-[#002FA7]"
                      >
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-[#A1A1AA]">—</span>
                  )}
                </div>
              </div>
              
              {/* Workflow Status */}
              {viewDoc.workflow_status && (
                <div className="p-3 bg-[#F4F4F5] rounded-sm">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-2">
                    Stato Workflow
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {viewDoc.workflow_status === 'pending' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                          <Clock size={12} />
                          {t('documents.workflowStatus.pending')}
                        </span>
                      )}
                      {viewDoc.workflow_status === 'changes_requested' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                          <PencilSimple size={12} />
                          {t('documents.workflowStatus.changes_requested')}
                        </span>
                      )}
                      {(viewDoc.workflow_status === 'approved' || viewDoc.workflow_status === 'archived') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                          <CheckCircle size={12} weight="fill" />
                          {t('documents.workflowStatus.approved')}
                        </span>
                      )}
                      {viewDoc.workflow_status === 'rejected' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                          <XCircle size={12} />
                          {t('documents.workflowStatus.rejected')}
                        </span>
                      )}
                      {viewDoc.approved_by_email && (
                        <span className="text-xs text-[#52525B]">
                          da {viewDoc.approved_by_email}
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        setViewDoc(null);
                        loadWorkflowHistory(viewDoc);
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      disabled={loadingHistory}
                    >
                      <FlowArrow size={16} className="mr-1" />
                      Storico Workflow
                    </Button>
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">
                  {t('documents.hash')}
                </p>
                <p className="hash-display text-xs break-all">{viewDoc.hash_sha256}</p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                {isPreviewable(viewDoc.content_type) && (
                  <Button
                    onClick={() => {
                      setViewDoc(null);
                      handlePreview(viewDoc);
                    }}
                    variant="outline"
                    className="rounded-sm"
                  >
                    <Eye size={18} className="mr-2" />
                    {t('documents.preview')}
                  </Button>
                )}
                <Button
                  onClick={() => handleDownload(viewDoc)}
                  className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                >
                  <DownloadSimple size={18} className="mr-2" />
                  {t('documents.download')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Document Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={closePreview}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">
              {previewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          {previewUrl && previewDoc && (
            <div className="max-h-[70vh] overflow-auto">
              {previewDoc.content_type.includes('pdf') ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-[70vh]"
                  title={previewDoc.original_filename}
                />
              ) : previewDoc.content_type.includes('image') ? (
                <img 
                  src={previewUrl} 
                  alt={previewDoc.original_filename}
                  className="max-w-full mx-auto"
                />
              ) : previewDoc.content_type.includes('text') ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-[70vh] bg-white"
                  title={previewDoc.original_filename}
                />
              ) : (
                <p className="text-center py-8 text-[#52525B]">
                  {t('documents.noPreview')}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF/A Preview Dialog */}
      <Dialog open={!!previewPdfaDoc} onOpenChange={closePreviewPdfa}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={20} className="text-green-600" />
              {previewPdfaDoc?.original_filename} - PDF/A
            </DialogTitle>
          </DialogHeader>
          
          <div className="min-h-[400px] max-h-[60vh] overflow-auto bg-[#F4F4F5] rounded-sm flex items-center justify-center">
            {previewPdfaUrl ? (
              <iframe
                src={previewPdfaUrl}
                className="w-full h-[60vh] border-0"
                title={`${previewPdfaDoc?.original_filename} - PDF/A`}
              />
            ) : (
              <p className="text-sm text-[#52525B]">{t('common.loading')}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={() => previewPdfaDoc && handleDownloadPdfa(previewPdfaDoc)}
              className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
            >
              <DownloadSimple size={18} className="mr-2" />
              {t('documents.downloadPdfa')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tags Dialog */}
      <Dialog open={!!editTagsDoc} onOpenChange={() => setEditTagsDoc(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} />
              {t('documents.editTags')}
            </DialogTitle>
          </DialogHeader>
          {editTagsDoc && (
            <div className="space-y-4">
              <p className="text-sm text-[#52525B]">{editTagsDoc.original_filename}</p>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('documents.tags')}
                </Label>
                <Input
                  value={editedTags}
                  onChange={(e) => setEditedTags(e.target.value)}
                  placeholder={t('documents.tagsPlaceholder')}
                  className="input-swiss mt-1"
                />
                <p className="text-xs text-[#A1A1AA] mt-1">{t('documents.tagsHint')}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditTagsDoc(null)}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveTags}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow History Dialog */}
      <Dialog open={showWorkflowHistory} onOpenChange={setShowWorkflowHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlowArrow size={20} />
              Storico Workflow
            </DialogTitle>
          </DialogHeader>
          {workflowHistory && (
            <div className="space-y-4">
              <div className="bg-[#F4F4F5] p-3 rounded-sm">
                <p className="text-sm font-medium text-[#09090B]">{workflowHistory.document_name}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-[#52525B]">
                  <span>Richiesto da: <strong>{workflowHistory.requester_email}</strong></span>
                  <span>Approvatore: <strong>{workflowHistory.approver_email}</strong></span>
                </div>
              </div>
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {workflowHistory.history?.map((entry, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
                      {(entry.action === 'submitted' || entry.action === 'resubmitted') && (
                        <CaretRight size={16} className="text-[#002FA7]" />
                      )}
                      {entry.action === 'approve' && (
                        <CheckCircle size={16} className="text-green-600" />
                      )}
                      {entry.action === 'reject' && (
                        <XCircle size={16} className="text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-[#09090B]">
                          {entry.action === 'submitted' && 'Inviato per approvazione'}
                          {entry.action === 'resubmitted' && 'Reinviato per approvazione'}
                          {entry.action === 'approve' && 'Approvato'}
                          {entry.action === 'reject' && 'Rifiutato'}
                        </span>
                      </div>
                      <p className="text-xs text-[#52525B]">
                        {entry.user_email} • {formatDate(entry.timestamp, localeMap[language])}
                      </p>
                      {entry.notes && (
                        <p className="mt-1 text-sm text-[#52525B] bg-white p-2 rounded border border-[#E4E4E7]">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkflowHistory(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UploadSimple size={20} />
              {t('documents.upload')} - {currentFolder?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Drop zone */}
            <div 
              className="border-2 border-dashed border-[#E4E4E7] rounded-sm p-8 text-center hover:border-[#002FA7] transition-colors cursor-pointer"
              onClick={() => document.getElementById('folder-file-input').click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#002FA7]', 'bg-[#F4F4F5]'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('border-[#002FA7]', 'bg-[#F4F4F5]'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-[#002FA7]', 'bg-[#F4F4F5]');
                const files = Array.from(e.dataTransfer.files);
                setUploadFiles(prev => [...prev, ...files]);
              }}
            >
              <UploadSimple size={32} className="mx-auto text-[#A1A1AA] mb-2" />
              <p className="text-sm text-[#52525B]">{t('documents.dropFiles')}</p>
              <p className="text-xs text-[#A1A1AA] mt-1">{t('documents.orClick')}</p>
              <input
                id="folder-file-input"
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setUploadFiles(prev => [...prev, ...files]);
                  e.target.value = '';
                }}
              />
            </div>

            {/* File list */}
            {uploadFiles.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-auto">
                {uploadFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[#F4F4F5] rounded-sm">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-[#52525B]" />
                      <span className="text-sm truncate max-w-xs">{file.name}</span>
                      <span className="text-xs text-[#A1A1AA]">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      onClick={() => removeUploadFile(idx)}
                      className="p-1 hover:bg-[#E4E4E7] rounded-sm"
                    >
                      <X size={14} className="text-[#52525B]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Tags input */}
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('documents.tags')}
              </Label>
              <Input
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder={t('documents.tagsPlaceholder')}
                className="input-swiss mt-1"
              />
              <p className="text-xs text-[#A1A1AA] mt-1">{t('documents.tagsHelp')}</p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowUploadDialog(false);
                setUploadFiles([]);
                setUploadTags('');
              }}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || uploadFiles.length === 0}
              className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <UploadSimple size={18} className="mr-2" />
                  {t('documents.upload')} ({uploadFiles.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <AlertDialog open={!!duplicateFile} onOpenChange={() => {
        setDuplicateFile(null);
        setDuplicateExisting(null);
        setPendingUploads([]);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={24} className="text-amber-500" />
              {t('documents.duplicateDetected')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>{t('documents.duplicateMessage')}</p>
              {duplicateFile && duplicateExisting && (
                <div className="bg-[#F4F4F5] p-3 rounded-sm space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-[#52525B]" />
                    <span className="font-medium">{duplicateFile.name}</span>
                  </div>
                  <div className="text-sm text-[#52525B] grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">{t('documents.currentVersion')}:</span>
                      <p>v{duplicateExisting.version} • {formatBytes(duplicateExisting.size)}</p>
                    </div>
                    <div>
                      <span className="font-medium">Nuovo file:</span>
                      <p>{formatBytes(duplicateFile.size)}</p>
                    </div>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={() => handleDuplicateDecision('skip')}>
              {t('documents.skipFile')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDuplicateDecision('update')}
              className="bg-[#002FA7] hover:bg-[#002075]"
              data-testid="update-version-btn"
            >
              <ClockCounterClockwise size={16} className="mr-2" />
              {t('documents.updateVersion')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClockCounterClockwise size={24} className="text-[#002FA7]" />
              {t('documents.versionHistory')}
            </DialogTitle>
          </DialogHeader>
          
          {versionHistoryDoc && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-[#F4F4F5] rounded-sm">
                <FileText size={24} className="text-[#002FA7]" />
                <div>
                  <p className="font-medium text-sm">{versionHistoryDoc.original_filename}</p>
                  <p className="text-xs text-[#52525B]">
                    {t('documents.currentVersion')}: v{versionHistoryDoc.version}
                  </p>
                </div>
              </div>
              
              {loadingVersions ? (
                <div className="py-8 text-center text-[#52525B]">
                  {t('common.loading')}
                </div>
              ) : documentVersions.length === 0 ? (
                <div className="py-8 text-center text-[#52525B]">
                  {t('documents.noVersions')}
                </div>
              ) : (
                <div className="max-h-[300px] overflow-y-auto space-y-2">
                  {documentVersions.map((version, index) => (
                    <div
                      key={version.id}
                      className={`flex items-center justify-between p-3 border rounded-sm ${
                        index === 0 ? 'border-[#002FA7] bg-blue-50' : 'border-[#E4E4E7]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-[#002FA7] text-white' : 'bg-[#E4E4E7] text-[#52525B]'
                        }`}>
                          v{version.version}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {index === 0 && <Badge className="mr-2 bg-[#002FA7]">{t('documents.currentVersion')}</Badge>}
                            {formatBytes(version.size)}
                          </p>
                          <p className="text-xs text-[#52525B]">
                            {version.created_by_email} • {formatDate(version.created_at, localeMap[language])}
                          </p>
                          {version.has_pdfa && (
                            <Badge variant="outline" className="mt-1 text-xs text-green-600 border-green-600">
                              PDF/A
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadVersion(version.id)}
                        className="rounded-sm"
                        data-testid={`download-version-${version.version}`}
                      >
                        <DownloadSimple size={16} className="mr-1" />
                        {t('documents.download')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionHistory(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
